<div class="h-screen relative overflow-hidden flex flex-col justify-between bg-[#f5f5f7] dark:bg-[#050505] text-slate-900 dark:text-gray-100 transition-colors duration-500 text-[14px] leading-normal antialiased selection:bg-indigo-500/30">

  <!-- Navigation -->
  <nav class="fixed top-0 w-full z-50 bg-[#f5f5f7]/90 dark:bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-gray-200 dark:border-white/5 transition-all duration-300">
    <div class="w-full px-6 h-[64px] flex items-center justify-between">
      <div class="flex items-center gap-4 group">
        <div class="w-8 h-8 rounded-lg bg-slate-900 dark:bg-white text-white dark:text-black flex items-center justify-center shadow-sm">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6.429 9.75 2.25 12l4.179 2.25m0-4.5 5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0 4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0-5.571 3-5.571-3" />
          </svg>
        </div>
        <div class="flex items-center gap-3">
          <span class="text-gray-300 dark:text-gray-700 font-light text-xl">/</span>
          <span class="font-sans font-medium text-[15px] tracking-tight text-slate-900 dark:text-white">Photo QC Analysis</span>
        </div>
      </div>
      
      <div class="flex items-center gap-6">
        <!-- Theme Toggles -->
        <div class="flex items-center p-1 rounded-full bg-gray-100/80 dark:bg-white/5 border border-gray-200/50 dark:border-white/5 backdrop-blur-md">
          <button (click)="setTheme('light')" [ngClass]="{'text-indigo-600 bg-white shadow-sm': currentTheme === 'light'}" class="p-1.5 rounded-full text-gray-400 hover:text-gray-900 dark:hover:text-white transition-all duration-200 hover:bg-white dark:hover:bg-white/10">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />
            </svg>
          </button>
          <button (click)="setTheme('dark')" [ngClass]="{'shadow-sm': currentTheme === 'dark'}" class="p-1.5 rounded-full text-gray-400 hover:text-gray-900 dark:hover:text-white transition-all duration-200 hover:bg-white dark:hover:bg-white/10">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" />
            </svg>
          </button>
        </div>

        <div class="hidden md:flex items-center gap-6">
          <a routerLink="/analysis-report" class="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 font-medium text-[13px] transition-colors">
            Photo QC Analysis
          </a>
          <button (click)="goToDocumentation()" class="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 font-medium text-[13px] transition-colors cursor-pointer">
            Documentation
          </button>
        </div>
      </div>
    </div>
  </nav>

  <!-- Main Content -->
  <main class="relative z-10 pt-[64px] w-full h-full flex flex-col flex-grow overflow-hidden transition-colors duration-500">
    
    <div class="w-full fade-in mx-auto h-full overflow-y-auto hide-scroll pb-24 px-8 pt-10">
      
      <!-- Header Section with Stats Cards -->
      <div class="flex items-start justify-between gap-6 mb-4">
        <!-- Left: Title -->
        <div class="flex flex-col items-start flex-1">
          <div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100/80 dark:border-indigo-500/20 mb-4 shadow-sm">
            <span class="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
            <span class="text-indigo-600 dark:text-indigo-400 text-[10px] font-bold uppercase tracking-widest">Photo QC Analysis</span>
          </div>
          
          <h2 class="text-3xl font-semibold tracking-tight text-[#0F172A] dark:text-white mb-2">
            Survey List
          </h2>
          <p class="text-gray-500 dark:text-gray-400 text-base font-normal leading-relaxed">
            View and analyze survey confidence scores across all templates.
          </p>
        </div>

        <!-- Right: Stats Cards -->
        <div *ngIf="templates.length > 0" class="flex gap-3 flex-shrink-0">
          <!-- Total Surveys Card - Purple -->
          <div class="bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl p-4 min-w-[140px] shadow-md shadow-violet-500/20 relative overflow-hidden">
            <div class="flex items-start justify-between mb-2">
              <p class="text-white/90 font-medium text-xs">Total Surveys</p>
              <div class="flex items-center gap-1 text-white/70 text-[10px]">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941" />
                </svg>
                <span>All time</span>
              </div>
            </div>
            <p class="text-2xl font-bold text-white mb-2">{{ totalSurveys }}</p>
            <div class="flex items-end justify-between">
              <span class="text-white/60 text-[10px] hover:text-white cursor-pointer transition-colors">See all</span>
              <div class="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
                <svg class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z" />
                </svg>
              </div>
            </div>
          </div>

          <!-- High Confidence Card - Green -->
          <div class="bg-gradient-to-br from-emerald-400 to-green-500 rounded-xl p-4 min-w-[140px] shadow-md shadow-emerald-500/20 relative overflow-hidden">
            <div class="flex items-start justify-between mb-2">
              <p class="text-white/90 font-medium text-xs">High</p>
              <div class="flex items-center gap-1 text-white/70 text-[10px]">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941" />
                </svg>
                <span>≥85%</span>
              </div>
            </div>
            <p class="text-2xl font-bold text-white mb-2">{{ highConfidenceCount }}</p>
            <div class="flex items-end justify-between">
              <span class="text-white/60 text-[10px] hover:text-white cursor-pointer transition-colors">See all</span>
              <div class="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
                <svg class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                </svg>
              </div>
            </div>
          </div>

          <!-- Medium Confidence Card - Orange -->
          <div class="bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl p-4 min-w-[140px] shadow-md shadow-amber-500/20 relative overflow-hidden">
            <div class="flex items-start justify-between mb-2">
              <p class="text-white/90 font-medium text-xs">Medium</p>
              <div class="flex items-center gap-1 text-white/70 text-[10px]">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14" />
                </svg>
                <span>70-84%</span>
              </div>
            </div>
            <p class="text-2xl font-bold text-white mb-2">{{ mediumConfidenceCount }}</p>
            <div class="flex items-end justify-between">
              <span class="text-white/60 text-[10px] hover:text-white cursor-pointer transition-colors">See all</span>
              <div class="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
                <svg class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
              </div>
            </div>
          </div>

          <!-- Low Confidence Card - Red -->
          <div class="bg-gradient-to-br from-rose-400 to-red-500 rounded-xl p-4 min-w-[140px] shadow-md shadow-red-500/20 relative overflow-hidden">
            <div class="flex items-start justify-between mb-2">
              <p class="text-white/90 font-medium text-xs">Low</p>
              <div class="flex items-center gap-1 text-white/70 text-[10px]">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6 9 12.75l4.286-4.286a11.948 11.948 0 0 1 4.306 6.43l.776 2.898m0 0 3.182-5.511m-3.182 5.51-5.511-3.181" />
                </svg>
                <span>&lt;70%</span>
              </div>
            </div>
            <p class="text-2xl font-bold text-white mb-2">{{ lowConfidenceCount }}</p>
            <div class="flex items-end justify-between">
              <span class="text-white/60 text-[10px] hover:text-white cursor-pointer transition-colors">See all</span>
              <div class="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
                <svg class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Confidence Score Legend - Right Aligned -->
      <div class="flex justify-end mb-6">
        <div class="flex items-center gap-6">
          <div class="flex items-center gap-2">
            <span class="w-3 h-3 rounded-full bg-emerald-500"></span>
            <span class="text-sm text-gray-600 dark:text-gray-400">≥85%</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="w-3 h-3 rounded-full bg-amber-500"></span>
            <span class="text-sm text-gray-600 dark:text-gray-400">70-84%</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="w-3 h-3 rounded-full bg-red-500"></span>
            <span class="text-sm text-gray-600 dark:text-gray-400">&lt;70%</span>
          </div>
        </div>
      </div>

      <!-- Dashboard Card -->
      <div class="bg-white dark:bg-[#0d0d0d]/80 dark:backdrop-blur-xl rounded-2xl overflow-hidden shadow-[0_8px_40px_-12px_rgba(0,0,0,0.1)] dark:shadow-[0_8px_40px_-8px_rgba(0,0,0,0.5)] border border-gray-100/80 dark:border-white/[0.06] transition-all duration-300">
        
        <!-- Search Bar -->
        <div class="px-8 py-4 border-b border-gray-100/80 dark:border-white/[0.06] flex items-center justify-between gap-4">
          <div class="relative max-w-md flex-1">
            <svg class="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
            </svg>
            <input
              type="text"
              [(ngModel)]="searchQuery"
              placeholder="Search surveys by name or ID..."
              class="w-full pl-11 pr-4 py-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200/80 dark:border-white/10 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500/50 transition-all text-sm"
            />
          </div>
        </div>

        <!-- Table Header (Sortable) -->
        <div class="grid grid-cols-[1fr_120px_160px_140px_120px] gap-4 items-center px-8 py-4 bg-gray-50/60 dark:bg-white/[0.02] border-b border-gray-100/60 dark:border-white/[0.04]">
          <button (click)="toggleSort('templateName')" class="flex items-center gap-1.5 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.08em] hover:text-gray-600 dark:hover:text-gray-300 transition-colors text-left">
            Survey Name
            <svg *ngIf="sortColumn === 'templateName'" class="w-3 h-3" [class.rotate-180]="sortDirection === 'desc'" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
            </svg>
          </button>
          <button (click)="toggleSort('questionCount')" class="flex items-center justify-center gap-1.5 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.08em] hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
            Questions
            <svg *ngIf="sortColumn === 'questionCount'" class="w-3 h-3" [class.rotate-180]="sortDirection === 'desc'" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
            </svg>
          </button>
          <button (click)="toggleSort('confidenceScore')" class="flex items-center justify-center gap-1.5 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.08em] hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
            Confidence
            <svg *ngIf="sortColumn === 'confidenceScore'" class="w-3 h-3" [class.rotate-180]="sortDirection === 'desc'" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
            </svg>
          </button>
          <div class="text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.08em] text-center">Last Run</div>
          <div class="text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.08em] text-center"></div>
        </div>

        <!-- Loading State -->
        <div *ngIf="isLoading" class="flex flex-col items-center justify-center py-20">
          <div class="relative">
            <div class="w-14 h-14 rounded-full border-[3px] border-gray-100 dark:border-gray-800"></div>
            <div class="absolute top-0 left-0 w-14 h-14 rounded-full border-[3px] border-transparent border-t-indigo-500 animate-spin"></div>
          </div>
          <p class="mt-5 text-sm text-gray-500 dark:text-gray-400 font-medium">Loading templates...</p>
        </div>

        <!-- Survey List -->
        <div *ngIf="!isLoading" class="divide-y divide-gray-100/60 dark:divide-white/[0.04]">
          
          <!-- Empty State -->
          <div *ngIf="filteredTemplates.length === 0" class="flex flex-col items-center justify-center py-20 px-8">
            <div class="w-20 h-20 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-50 dark:from-white/10 dark:to-white/5 flex items-center justify-center mb-5 shadow-inner">
              <svg class="w-10 h-10 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Templates Found</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 text-center max-w-md">
              No analysis reports are available at this time.
            </p>
          </div>

          <!-- Survey Items -->
          <div *ngFor="let template of filteredTemplates; let i = index"
               class="grid grid-cols-[1fr_120px_160px_140px_120px] gap-4 items-center px-8 py-5"
               [ngClass]="{'bg-gray-50/40 dark:bg-white/5': i % 2 === 0}">
            <div class="flex items-center gap-4">
              <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-500/10 dark:to-violet-500/10 flex items-center justify-center text-indigo-500 dark:text-indigo-400 shadow-sm border border-indigo-100/50 dark:border-indigo-500/20">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z" />
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <h3 (click)="viewQuestions(template)" class="text-[15px] font-semibold text-gray-800 dark:text-gray-100 tracking-tight truncate cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">{{ template.templateName }}</h3>
                <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">ID: {{ template.templateId }}</p>
              </div>
            </div>
            
            <div class="flex justify-center">
              <span class="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-gray-100/80 dark:bg-white/[0.06] text-gray-600 dark:text-gray-300 text-sm font-bold">
                {{ template.questionCount }}
              </span>
            </div>
            
            <div class="flex items-center justify-end gap-3">
              <span class="text-xs text-gray-400 dark:text-gray-500">Confidence</span>
              <!-- Donut Chart -->
              <div class="relative w-14 h-14">
                <div class="w-full h-full rounded-full" 
                     [style.background]="currentTheme === 'dark' ? getDonutStyleDark(template.confidenceScore) : getDonutStyle(template.confidenceScore)">
                </div>
                <div class="absolute inset-[5px] rounded-full bg-white dark:bg-[#0d0d0d] flex items-center justify-center">
                  <span class="text-[10px] font-bold" [style.color]="getScoreColor(template.confidenceScore)">
                    {{ getConfidenceLabel(template.confidenceScore) }}
                  </span>
                </div>
              </div>
            </div>
            
            <!-- Last Run Date -->
            <div class="flex justify-center">
              <span *ngIf="template.lastRunDate" class="text-[11px] text-gray-500 dark:text-gray-400">
                {{ formatLastRunDate(template.lastRunDate) }}
              </span>
              <span *ngIf="!template.lastRunDate" class="text-[10px] font-medium px-2 py-1 rounded-md text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10">
                Not run yet
              </span>
            </div>
            
            <!-- View Details Button -->
            <div class="flex justify-center">
              <div class="relative group/view">
                <button 
                  (click)="viewQuestions(template)"
                  class="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-white/10 dark:hover:bg-white/20 text-gray-700 dark:text-gray-200 text-[13px] font-semibold transition-all duration-200">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                  </svg>
                  <span>View</span>
                </button>
                <!-- Tooltip -->
                <div class="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg opacity-0 group-hover/view:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                  View question details
                  <div class="absolute top-full right-4 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                </div>
              </div>
            </div>
          </div>

        </div>
        
        <!-- Footer Stats -->
        <div class="px-8 py-5 bg-gray-50/60 dark:bg-white/[0.02] border-t border-gray-100/60 dark:border-white/[0.04]">
          <div class="flex items-center justify-between text-sm">
            <span class="text-gray-500 dark:text-gray-400">{{ filteredTemplates.length }} templates</span>
            <span class="text-gray-400 dark:text-gray-500">Last updated: Just now</span>
          </div>
        </div>
      </div>

    </div>
  </main>

</div>
